import { useState } from 'react';
import { Link } from 'react-router-dom';
import AuthBrandLogo from '../../branding/AuthBrandLogo';
import { identityApi } from '../../api/client';
import { ROUTES } from '../../routes/constants';

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-brand/[0.06] blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-info/[0.05] blur-[100px]" />
        <div className="absolute top-1/2 -left-32 w-[350px] h-[350px] rounded-full bg-bull/[0.03] blur-[80px]" />
      </div>
      <div className="w-full max-w-sm relative z-10">{children}</div>
    </div>
  );
}

export default function ForgotPasswordPage() {
  const [email, setEmail]     = useState('');
  const [sent, setSent]       = useState(false);
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError('Please enter your email address.'); return; }
    setError('');
    setLoading(true);
    try {
      await identityApi.forgotPassword(email);
      setSent(true);
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <AuthShell>
        <div className="text-center mb-8">
          <AuthBrandLogo />
        </div>
        <div className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-6 space-y-4 shadow-modal overflow-hidden text-center">
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />
          <h2 className="font-semibold text-txt-primary text-sm">Check your email</h2>
          <p className="text-xs text-txt-secondary">
            If an account with <span className="font-mono text-txt-primary">{email}</span> exists,
            reset instructions have been sent.
          </p>
          <div className="border-t border-bg-border/40 pt-3">
            <Link to={ROUTES.LOGIN} className="text-xs text-txt-muted hover:text-brand transition-colors">
              Back to sign in
            </Link>
          </div>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="text-center mb-8">
        <AuthBrandLogo />
        <p className="text-2xs font-mono text-txt-muted tracking-widest uppercase mt-3">
          Reset your password
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-6 space-y-5 shadow-modal overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />

        <div className="space-y-1.5">
          <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">Email</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            className="input-base"
            disabled={loading}
          />
        </div>

        {error && (
          <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{error}</p>
        )}

        <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-1">
          {loading ? 'Sending…' : 'Send Reset Instructions'}
        </button>

        <div className="border-t border-bg-border/40 pt-3 text-center">
          <Link to={ROUTES.LOGIN} className="text-xs text-txt-muted hover:text-brand transition-colors">
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
